import { collection, query, orderBy, limit, getDocs, where } from 'firebase/firestore';
import { db } from './firebase';
import { LeaderboardEntry } from '../types';

/**
 * Get global leaderboard (top players by XP)
 */
export const getGlobalLeaderboard = async (limitCount: number = 100): Promise<LeaderboardEntry[]> => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(
      usersRef,
      orderBy('xp', 'desc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        uid: data.uid,
        displayName: data.displayName,
        avatar: data.avatar,
        rank: data.rank,
        xp: data.xp,
        totalScore: data.stats?.totalScore || 0,
        gamesWon: data.stats?.gamesWon || 0,
        winRate: data.multiplayerStats?.winRate || 0,
      };
    });
  } catch (error) {
    console.error('❌ Error fetching global leaderboard:', error);
    throw error;
  }
};

/**
 * Get multiplayer leaderboard (top players by multiplayer wins)
 */
export const getMultiplayerLeaderboard = async (limitCount: number = 100): Promise<LeaderboardEntry[]> => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(
      usersRef,
      orderBy('multiplayerStats.wins', 'desc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        uid: data.uid,
        displayName: data.displayName,
        avatar: data.avatar,
        rank: data.rank,
        xp: data.xp,
        totalScore: data.multiplayerStats?.totalScore || 0,
        gamesWon: data.multiplayerStats?.wins || 0,
        winRate: data.multiplayerStats?.winRate || 0,
      };
    });
  } catch (error) {
    console.error('❌ Error fetching multiplayer leaderboard:', error);
    throw error;
  }
};

/**
 * Get single player leaderboard (top players by best score)
 */
export const getSinglePlayerLeaderboard = async (limitCount: number = 100): Promise<LeaderboardEntry[]> => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(
      usersRef,
      orderBy('stats.bestScore', 'desc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        uid: data.uid,
        displayName: data.displayName,
        avatar: data.avatar,
        rank: data.rank,
        xp: data.xp,
        totalScore: data.stats?.totalScore || 0,
        gamesWon: data.stats?.gamesWon || 0,
        winRate: data.multiplayerStats?.winRate || 0,
      };
    });
  } catch (error) {
    console.error('❌ Error fetching single player leaderboard:', error);
    throw error;
  }
};

/**
 * Get user's rank/position on leaderboard
 */
export const getUserRank = async (userId: string, leaderboardType: 'global' | 'multiplayer' | 'singleplayer' = 'global'): Promise<number> => {
  try {
    const usersRef = collection(db, 'users');
    let orderByField = 'xp';

    if (leaderboardType === 'multiplayer') {
      orderByField = 'multiplayerStats.wins';
    } else if (leaderboardType === 'singleplayer') {
      orderByField = 'stats.bestScore';
    }

    // Get user's value
    const userDoc = await getDocs(query(usersRef, where('uid', '==', userId), limit(1)));

    if (userDoc.empty) {
      return -1;
    }

    const userData = userDoc.docs[0].data();
    let userValue = 0;

    if (leaderboardType === 'global') {
      userValue = userData.xp || 0;
    } else if (leaderboardType === 'multiplayer') {
      userValue = userData.multiplayerStats?.wins || 0;
    } else {
      userValue = userData.stats?.bestScore || 0;
    }

    // Count how many users are above this user
    const higherUsersQuery = query(
      usersRef,
      where(orderByField, '>', userValue)
    );

    const higherUsersSnapshot = await getDocs(higherUsersQuery);

    return higherUsersSnapshot.size + 1; // +1 because rank is 1-indexed
  } catch (error) {
    console.error('❌ Error getting user rank:', error);
    return -1;
  }
};
